import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Save, Loader2, Tag, Layers, Search } from 'lucide-react'
import { z } from 'zod'
import { Controller } from 'react-hook-form'
import { PageHeader } from '@/components/ui/PageHeader'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Button } from '@/components/ui/Button'
import { NumberInput } from '@/components/ui/NumberInput'
import { CurrencyInput } from '@/components/ui/CurrencyInput'
import { PercentInput } from '@/components/ui/PercentInput'
import { StatusToggle } from '@/components/ui/StatusToggle'
import { Toggle } from '@/components/ui/Toggle'
import { FormSidemenu, type FormSection } from '@/components/ui/FormSidemenu'
import { useZodForm } from '@/hooks/useZodForm'
import { useAuthority } from '@/hooks/useAuthority'
import { applyApiErrorsToForm } from '@/lib/api-errors'
import { onValidationFail } from '@/lib/form-helpers'
import { DiscountCouponsService, type DiscountCoupon } from '@/services/modules/DiscountCouponsService'
import { PlansService, type Plan } from '@/services/modules/PlansService'
import { toast } from '@/components/ui/Toast'
import { cn } from '@/lib/cn'
import { normalizeForMatch } from '@/lib/validators'

/**
 * Form de Cupom de Desconto em página dedicada (regra global do Comercial),
 * com FormSidemenu separando:
 *   - Dados: código, desconto, parcelas (com switcher "Sem limite"), vigência, status
 *   - Planos aplicáveis: lista de toggles 1/linha com busca + marcar/desmarcar todos
 */
/** Código só com letras e números (sem acento/espaço/pontuação), em maiúsculas. */
const normalizeCouponCode = (v: string) =>
  (v ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9]/g, '').toUpperCase()

const schema = z.object({
  code:         z.string().trim().min(1, 'Informe o código do cupom.').max(40, 'Máximo 40 caracteres.').transform(normalizeCouponCode),
  value:        z.number({ message: 'Informe o valor do desconto.' }).min(0, 'O valor não pode ser negativo.'),
  discountType: z.enum(['PORCENTAGEM', 'VALOR']),
  // null = "Sem limite" (aplica a todas as parcelas). O mínimo (>= 1) é checado
  // no superRefine SÓ quando "Sem limite" está desligado — assim marcar "Sem
  // limite" nunca dispara validação neste campo.
  monthlyInstallments: z.number().int().nullable(),
  noInstallmentsLimit: z.boolean(),
  startDate:    z.string().nullable(),
  endDate:      z.string().nullable(),
  status:       z.enum(['ATIVO', 'INATIVO']),
  planIds:      z.array(z.string()),
}).superRefine((val, ctx) => {
  if (val.discountType === 'PORCENTAGEM' && val.value > 100) {
    ctx.addIssue({ code: 'custom', path: ['value'], message: 'Porcentagem não pode passar de 100%.' })
  }
  if (!val.noInstallmentsLimit && (val.monthlyInstallments == null || val.monthlyInstallments < 1)) {
    ctx.addIssue({ code: 'custom', path: ['monthlyInstallments'], message: 'Informe as parcelas ou marque "Sem limite".' })
  }
  if (val.startDate && val.endDate && val.endDate < val.startDate) {
    ctx.addIssue({ code: 'custom', path: ['endDate'], message: 'A data final deve ser igual ou posterior à inicial.' })
  }
})
type FormValues = z.infer<typeof schema>

export function DiscountCouponFormPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id?: string }>()
  const isEdit = !!id
  const { can } = useAuthority()
  const canSave = can(isEdit ? 'commercial.discount_coupons.edit' : 'commercial.discount_coupons.create')
  const [loading, setLoading] = useState(isEdit)
  const [plans, setPlans] = useState<Plan[]>([])
  const [activeSection, setActiveSection] = useState('data')
  const [planSearch, setPlanSearch] = useState('')

  const form = useZodForm(schema, {
    defaultValues: {
      code: '',
      value: 0,
      discountType: 'PORCENTAGEM',
      monthlyInstallments: 1,
      noInstallmentsLimit: false,
      startDate: null,
      endDate: null,
      status: 'ATIVO',
      planIds: [],
    },
  })
  const discountType        = form.watch('discountType')
  const planIds             = form.watch('planIds')
  const noInstallmentsLimit = form.watch('noInstallmentsLimit')

  // Carrega planos ativos para a seleção
  useEffect(() => {
    PlansService.list({ size: 100, status: 'ATIVO' })
      .then(r => setPlans(r.data))
      .catch(() => setPlans([]))
  }, [])

  useEffect(() => {
    if (!isEdit || !id) return
    setLoading(true)
    DiscountCouponsService.show(id)
      .then((c: DiscountCoupon) => {
        form.reset({
          code:         c.code,
          value:        c.value,
          discountType: c.discountType,
          // Parcelas 0/null = "Sem limite".
          monthlyInstallments: c.monthlyInstallments && c.monthlyInstallments > 0 ? c.monthlyInstallments : null,
          noInstallmentsLimit: c.monthlyInstallments == null || c.monthlyInstallments <= 0,
          startDate:    c.startDate,
          endDate:      c.endDate,
          status:       c.status,
          planIds:      (c.plans ?? []).map(p => p.id),
        })
      })
      .catch(() => toast.error({ title: 'Não encontrado', description: 'Cupom não localizado.' }))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, id])

  // Lista de planos filtrada por busca (case+accent insensitive — regra global)
  const filteredPlans = useMemo(() => {
    const q = normalizeForMatch(planSearch)
    if (!q) return plans
    return plans.filter(p => normalizeForMatch(p.name).includes(q))
  }, [plans, planSearch])

  const allFilteredSelected = filteredPlans.length > 0 && filteredPlans.every(p => planIds.includes(p.id))

  function toggleAllFiltered() {
    const ids = new Set(planIds)
    if (allFilteredSelected) {
      filteredPlans.forEach(p => ids.delete(p.id))
    } else {
      filteredPlans.forEach(p => ids.add(p.id))
    }
    form.setValue('planIds', Array.from(ids), { shouldValidate: true })
  }
  function togglePlan(pid: string, next: boolean) {
    const ids = new Set(planIds)
    if (next) ids.add(pid); else ids.delete(pid)
    form.setValue('planIds', Array.from(ids), { shouldValidate: true })
  }

  async function onSubmit(values: FormValues) {
    if (!canSave) return
    try {
      const payload = {
        code:                 values.code,
        value:                values.value,
        discount_type:        values.discountType,
        monthly_installments: values.noInstallmentsLimit ? null : values.monthlyInstallments,
        start_date:           values.startDate || null,
        end_date:             values.endDate || null,
        status:               values.status,
        plan_ids:             values.planIds,
      }
      if (isEdit && id) await DiscountCouponsService.update(id, payload as Partial<DiscountCoupon>)
      else              await DiscountCouponsService.create(payload as Partial<DiscountCoupon>)

      toast.success({
        title: isEdit ? 'Cupom atualizado' : 'Cupom criado',
        description: isEdit ? 'As alterações foram salvas.' : 'O novo cupom foi cadastrado.',
      })
      navigate('/commercial/discount-coupons')
    } catch (e) {
      applyApiErrorsToForm(e, form.setError, {
        discount_type:        'discountType',
        monthly_installments: 'monthlyInstallments',
        start_date:           'startDate',
        end_date:             'endDate',
        plan_ids:             'planIds',
      })
    }
  }

  // ===================== Sections =====================

  const dataSection = (
    <SectionShell title="Dados" description="Configuração do desconto, parcelas e vigência.">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Controller name="code" control={form.control} render={({ field, fieldState }) => (
          <Input
            label="Código"
            required
            wrapperClassName="md:col-span-2"
            value={field.value ?? ''}
            onChange={(e) => field.onChange(normalizeCouponCode(e.target.value))}
            error={fieldState.error?.message}
            placeholder="PROMO10"
            hint="Apenas letras e números (sem acentos, espaços ou pontuação)."
          />
        )} />

        <Controller
          name="discountType"
          control={form.control}
          render={({ field }) => (
            <Select
              label="Tipo de desconto"
              required
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
              options={[
                { value: 'PORCENTAGEM', label: 'Porcentagem (%)' },
                { value: 'VALOR',       label: 'Valor fixo (R$)' },
              ]}
              searchable={false}
              clearable={false}
            />
          )}
        />
        <Controller
          name="value"
          control={form.control}
          render={({ field, fieldState }) => (
            discountType === 'PORCENTAGEM'
              ? <PercentInput  label="Valor do desconto" required value={field.value ?? null} onChange={(v) => field.onChange(v ?? 0)} error={fieldState.error?.message} />
              : <CurrencyInput label="Valor do desconto" required value={field.value ?? null} onChange={(v) => field.onChange(v ?? 0)} error={fieldState.error?.message} />
          )}
        />

        <Controller
          name="monthlyInstallments"
          control={form.control}
          render={({ field, fieldState }) => (
            <FieldWithSwitch
              label="Parcelas aplicáveis"
              required={!noInstallmentsLimit}
              disabled={noInstallmentsLimit}
              switchLabel="Sem limite"
              switchValue={noInstallmentsLimit}
              onSwitchChange={(v) => {
                form.setValue('noInstallmentsLimit', v, { shouldValidate: true })
                // Ao ligar "Sem limite": limpa o campo e remove qualquer erro dele.
                if (v) {
                  form.setValue('monthlyInstallments', null, { shouldValidate: false })
                  form.clearErrors('monthlyInstallments')
                }
              }}
            >
              <NumberInput
                value={field.value}
                onChange={field.onChange}
                disabled={noInstallmentsLimit}
                placeholder={noInstallmentsLimit ? '— Sem limite —' : '12'}
                min={1}
                max={60}
                error={fieldState.error?.message}
              />
            </FieldWithSwitch>
          )}
        />
        <div />

        <Input
          label="Início da vigência"
          type="date"
          {...form.register('startDate')}
          error={form.formState.errors.startDate?.message}
          hint="Opcional. Antes desta data o cupom não vale."
        />
        <Input
          label="Fim da vigência"
          type="date"
          {...form.register('endDate')}
          error={form.formState.errors.endDate?.message}
          hint="Opcional. Após esta data o cupom expira."
        />

        <Controller
          name="status"
          control={form.control}
          render={({ field }) => (
            <div className="md:col-span-2 border-t border-gray-100 pt-4 mt-2">
              <StatusToggle value={field.value} onChange={field.onChange} />
            </div>
          )}
        />
      </div>
    </SectionShell>
  )

  const plansSection = (
    <SectionShell
      title="Planos aplicáveis"
      description="Selecione os planos em que este cupom pode ser usado. Deixe tudo desmarcado para aplicar a todos os planos."
    >
      {plans.length === 0 ? (
        <div className="rounded-md border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
          Nenhum plano ativo cadastrado.
        </div>
      ) : (
        <div className="rounded-md border border-gray-200">
          {/* Toolbar: busca + marcar/desmarcar todos */}
          <div className="flex items-center gap-2 border-b border-gray-200 p-2">
            <div className="relative flex-1">
              <Search size={14} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={planSearch}
                onChange={e => setPlanSearch(e.target.value)}
                placeholder="Buscar plano"
                className="w-full rounded-md border border-gray-200 bg-white py-1.5 pl-7 pr-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>
            <button
              type="button"
              onClick={toggleAllFiltered}
              className="cursor-pointer rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
            >
              {allFilteredSelected ? 'Desmarcar todos' : 'Marcar todos'}
            </button>
          </div>

          {/* Contador */}
          <div className="border-b border-gray-100 px-3 py-1.5 text-xs text-gray-500">
            {planIds.length} de {plans.length} selecionado{planIds.length === 1 ? '' : 's'}
          </div>

          {/* Lista 1/linha com scroll */}
          <div className="max-h-80 overflow-y-auto">
            {filteredPlans.length === 0 ? (
              <div className="p-4 text-center text-xs text-gray-400">Nenhum plano corresponde à busca.</div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {filteredPlans.map(p => (
                  <li key={p.id} className="flex items-center justify-between px-3 py-2">
                    <span className="text-sm text-gray-700">{p.name}</span>
                    <Toggle checked={planIds.includes(p.id)} onChange={(v) => togglePlan(p.id, v)} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </SectionShell>
  )

  const sections: FormSection[] = [
    { key: 'data',  label: 'Dados',             icon: Tag,    content: dataSection },
    { key: 'plans', label: 'Planos aplicáveis', icon: Layers, content: plansSection },
  ]

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={28} className="animate-spin text-gray-400" />
      </div>
    )
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit, onValidationFail)}>
      <PageHeader
        title={isEdit ? 'Editar cupom' : 'Novo cupom'}
        subtitle="Cupons aplicáveis em planos selecionados"
        crumbs={[{ label: 'Comercial' }, { label: 'Cupons', to: '/commercial/discount-coupons' }, { label: isEdit ? 'Editar' : 'Novo' }]}
        backTo="/commercial/discount-coupons"
        actions={<Button type="submit" loading={form.formState.isSubmitting} disabled={!canSave}
          title={canSave ? undefined : `Sem permissão para ${isEdit ? 'editar' : 'criar'} cupons`}
          iconLeft={<Save size={14} />}>Salvar</Button>}
      />
      <FormSidemenu sections={sections} activeKey={activeSection} onChange={setActiveSection} />
    </form>
  )
}

/**
 * Wrapper das seções — fundo branco + ring, padrão global.
 */
function SectionShell({ title, description, children }: {
  title: string
  description?: string
  children: React.ReactNode
}) {
  return (
    <div className="bg-white rounded-md ring-1 ring-gray-200 p-5 sm:p-6">
      <div className="mb-4">
        <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      </div>
      {children}
    </div>
  )
}

/**
 * Campo com switcher no canto superior direito (ex.: "Sem limite"), reusando
 * o padrão dos forms de comissão.
 */
function FieldWithSwitch({
  label, required, switchLabel, switchValue, onSwitchChange, disabled, children,
}: {
  label: string
  required?: boolean
  switchLabel: string
  switchValue: boolean
  onSwitchChange: (v: boolean) => void
  disabled?: boolean
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between mb-1">
        <label className={cn('text-xs font-medium', disabled ? 'text-gray-400' : 'text-gray-700')}>
          {label}
          {required && <span className="text-rose-500 ml-0.5">*</span>}
        </label>
        <Toggle checked={switchValue} onChange={onSwitchChange} label={switchLabel} size="sm" labelSize="sm" />
      </div>
      {children}
    </div>
  )
}
